/**
 * NPC tasks and coverage — the two things that override plain wandering.
 *
 * ## Tasks
 *
 * A task pins an NPC to one station for a window. It is how an author makes movement *mean*
 * something rather than being texture: an NPC that happens to be near the relay mast is scenery,
 * an NPC that is there every night between two and four because that is when the tide is out is a
 * fact a player can learn and use.
 *
 * First match wins, in declaration order, so the specific goes above the general — the same rule
 * as endings, and one an author can hold in their head.
 *
 * ## Coverage
 *
 * A server with nobody on it should still feel inhabited. Coverage NPCs are gated on the *live*
 * listener count, so they fill an empty band and step back when real people arrive.
 *
 * That is the honest version of "make it feel populated": rather than faking activity all the
 * time, the world is fullest exactly when it would otherwise be emptiest. It also keeps the
 * chapter's real question alive — a player alone at 3am who hears a voice cannot tell whether the
 * server is busy or whether the game is keeping them company, and both answers are unsettling.
 */
import { gatePasses, type StoryState } from '../story/gates.js';
import type { PackNpc, PackNpcTask } from '../content/schema.js';
import { resolveSchedule, type ScheduledSignal } from './matrix.js';
import type { WorldTime } from './clock.js';

/** How busy the band is, as the relay reports it. */
export interface BandPopulation {
  /** Other listeners on this band, excluding the player. -1 when unknown (relay unavailable). */
  others: number;
}

/** Nobody knows — the relay is not connected. */
export const UNKNOWN_POPULATION: BandPopulation = { others: -1 };

/**
 * The task an NPC is on, if any.
 *
 * Returns null when the NPC is simply wandering, which is the common case.
 */
export function activeTask(npc: PackNpc, time: WorldTime, state: StoryState): PackNpcTask | null {
  for (const task of npc.tasks ?? []) {
    if (!resolveSchedule(task.schedule, time).active) continue;
    if (!gatePasses(task.requires, state)) continue;
    return task;
  }
  return null;
}

/**
 * Should this coverage NPC be on the air?
 *
 * An unknown population counts as empty. That is deliberate: when the relay is unreachable the
 * player is, as far as the game can tell, alone — and the whole purpose of a coverage NPC is to
 * keep an empty band from being silent. Treating "unknown" as "busy" would silence the NPC in
 * exactly the case it exists for.
 */
export function coverageAllows(
  coverage: { maxOthers: number } | undefined,
  population: BandPopulation,
): boolean {
  if (!coverage) return true;
  if (population.others < 0) return true;
  return population.others <= coverage.maxOthers;
}

/**
 * Build the eligibility filter the matrix takes.
 *
 * Everything that decides whether a signal is on the air for *this* player, in one place: story
 * gates from Chapter 3, and now coverage. The matrix stays ignorant of both.
 */
export function signalEligibility(options: {
  population: BandPopulation;
  gateOpen: (requires: Parameters<typeof gatePasses>[0]) => boolean;
}) {
  return (signal: {
    requires?: Parameters<typeof gatePasses>[0];
    coverage?: { maxOthers: number };
  }): boolean => {
    if (!options.gateOpen(signal.requires)) return false;
    return coverageAllows(signal.coverage, options.population);
  };
}

/**
 * Apply whatever task an NPC is on to its signal, for one moment.
 *
 * The matrix cannot do this itself: a task depends on the player's story state and the clock,
 * and the matrix knows about neither. So it is a caller-supplied transform, the same shape of
 * seam as the story gate — everything that needs to know who the player is stays outside.
 *
 * A tasked NPC keeps its roaming block with the station list narrowed to one. That matters: drop
 * the block instead and the NPC would stop bursting and start holding the carrier down, which is
 * the difference between a person at a radio and a beacon. Working a task should not change what
 * they sound like, only where they are.
 */
export function applyTasks(
  npcs: readonly PackNpc[],
  // A getter, not a value: this transform is built once and then called on every dial poll, and
  // the player's state moves underneath it. A snapshot would pin every NPC to whatever task was
  // current when the radio was switched on.
  getState: () => StoryState,
): (signal: ScheduledSignal, time: WorldTime) => ScheduledSignal {
  const byId = new Map(npcs.map((npc) => [npc.id, npc]));

  return (signal, time) => {
    const npc = signal.npcId ? byId.get(signal.npcId) : undefined;
    if (!npc) return signal;

    const task = activeTask(npc, time, getState());
    if (!task) return signal;

    const roaming = signal.placement.roaming;
    return {
      ...signal,
      placement: {
        ...signal.placement,
        frequencyKhz: task.stationKhz,
        ...(roaming ? { roaming: { ...roaming, stations: [task.stationKhz] } } : {}),
      },
      ...(task.dialogue ? { dialogue: task.dialogue } : {}),
    };
  };
}

/**
 * Keep the NPC the player is actually talking to on the air.
 *
 * Sigrid is keyed up two in-game minutes in seven — ten real seconds at a time. That duty cycle
 * is right for someone sweeping frequencies alone, and catastrophic once a player answers: the
 * conversation would break off before they finished reading the line. Measured, not guessed; the
 * numbers are in the Chapter 6 status doc.
 *
 * So being in a conversation suspends the duty cycle, which is also what the fiction says. An NPC
 * bursting and listening is working; an NPC who has someone on the other end is talking to them.
 * Roaming itself is untouched — she still moves on when her dwell slot ends, and that is meant to
 * be able to happen mid-sentence.
 */
export function holdEngagedOnAir(
  isEngaged: (signalId: string) => boolean,
): (signal: ScheduledSignal) => ScheduledSignal {
  return (signal) => {
    const roaming = signal.placement.roaming;
    if (roaming?.transmitMinutes === undefined || !isEngaged(signal.id)) return signal;
    const { transmitMinutes: _dropped, ...held } = roaming;
    return { ...signal, placement: { ...signal.placement, roaming: held } };
  };
}
